import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { authApi, api, resolveImageUrl } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Upload, Plus, X } from "lucide-react";
import ImageFitControls from "@/components/admin/ImageFitControls";

const empty = () => ({ slug: "", name: "", description: "", image: "", image_fit: "cover", image_position: "center", order: 0 });

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminPutterSubcategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  const fileRefs = useRef([]);

  useEffect(() => {
    api.get("/site-settings")
      .then(({ data }) => setRows((data.putter_subcategories || []).map((r) => ({ ...empty(), ...r }))))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const patch = (i, p) => setRows((arr) => arr.map((r, j) => j === i ? { ...r, ...p } : r));

  const uploadImage = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch(idx, { image: resolveImageUrl(data.path) });
    } catch { toast.error("Upload failed"); }
    finally { setUploadingIdx(-1); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: cur } = await api.get("/site-settings");
      // Autogenerate slug for any row missing one
      const clean = rows
        .filter((r) => r.name.trim())
        .map((r, i) => ({ ...r, slug: r.slug || slugify(r.name), order: r.order ?? i }));
      const payload = { ...cur, putter_subcategories: clean };
      await authApi.put("/admin/site-settings", payload);
      toast.success("Putter families saved");
      setRows(clean);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div data-testid="admin-putter-subs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-2">Putter Sub-Categories</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage the Scottsdale · TEC · PLD Milled · G Le4 · Junior families shown on <code>/equipment/putters</code>.</p>
        </div>
        <button onClick={save} disabled={saving} className="bg-[#E31837] text-white px-6 py-3 label-caps btn-transition hover:bg-[#b3122c] disabled:opacity-60" data-testid="save-putter-subs-btn">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <div className="space-y-5">
        {rows.map((s, idx) => (
          <div key={idx} className="border border-border p-5" data-testid={`putter-sub-row-${idx}`}>
            <div className="grid md:grid-cols-[220px_1fr] gap-5">
              <div>
                <div className="h-32 bg-secondary border border-border overflow-hidden flex items-center justify-center">
                  {s.image ? <img src={s.image} alt="" className="w-full h-full" style={{ objectFit: s.image_fit || "cover", objectPosition: s.image_position || "center" }} /> : <span className="text-muted-foreground text-xs">No image</span>}
                </div>
                <input ref={(el) => (fileRefs.current[idx] = el)} type="file" accept="image/*" hidden onChange={(e) => uploadImage(idx, e.target.files[0])} data-testid={`putter-sub-image-input-${idx}`} />
                <button type="button" onClick={() => fileRefs.current[idx]?.click()} disabled={uploadingIdx === idx} className="w-full mt-2 border border-[#0A0A0A] py-2 label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-60" data-testid={`putter-sub-upload-${idx}`}>
                  {uploadingIdx === idx ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Image</>}
                </button>
                <div className="mt-3">
                  <ImageFitControls fit={s.image_fit} position={s.image_position} onChange={(p) => patch(idx, p)} testidPrefix={`putter-sub-${idx}-image`} />
                </div>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-[1fr_150px_80px_32px] gap-3">
                  <div>
                    <Label className="label-caps text-muted-foreground">Family Name</Label>
                    <Input value={s.name} onChange={(e) => patch(idx, { name: e.target.value })} placeholder="Scottsdale TEC" className="mt-1 rounded-none" data-testid={`putter-sub-name-${idx}`} />
                  </div>
                  <div>
                    <Label className="label-caps text-muted-foreground">Slug</Label>
                    <Input value={s.slug} onChange={(e) => patch(idx, { slug: e.target.value })} placeholder="auto" className="mt-1 rounded-none" data-testid={`putter-sub-slug-${idx}`} />
                  </div>
                  <div>
                    <Label className="label-caps text-muted-foreground">Order</Label>
                    <Input type="number" value={s.order} onChange={(e) => patch(idx, { order: parseInt(e.target.value, 10) || 0 })} className="mt-1 rounded-none" data-testid={`putter-sub-order-${idx}`} />
                  </div>
                  <button type="button" onClick={() => setRows((arr) => arr.filter((_, j) => j !== idx))} className="mt-6 text-muted-foreground hover:text-[#E31837]" aria-label="Remove" data-testid={`putter-sub-remove-${idx}`}><X className="h-4 w-4" /></button>
                </div>
                <div>
                  <Label className="label-caps text-muted-foreground">Description</Label>
                  <Textarea value={s.description} onChange={(e) => patch(idx, { description: e.target.value })} rows={4} className="mt-1 rounded-none" data-testid={`putter-sub-description-${idx}`} />
                </div>
              </div>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setRows((arr) => [...arr, empty()])} className="w-full border border-dashed border-border py-4 label-caps text-muted-foreground hover:text-[#0A0A0A] hover:border-[#0A0A0A] btn-transition flex items-center justify-center gap-2" data-testid="add-putter-sub-btn">
          <Plus className="h-4 w-4" /> Add Family
        </button>
      </div>
    </div>
  );
}
